import { gql } from '@apollo/client/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory, useLocation } from 'react-router';
import { LinkProps, Redirect } from 'react-router-dom';

import { LoadingImage } from 'component-lib/atoms/LoadingImage/LoadingImage';
import { Button } from 'play/atoms/button/Button';
import { isSkippingEnabled } from 'play/support/isSkippingEnabled';
import { useCustomParameters } from 'play/support/useCustomParameters';
import { useTrackAmplitudeEvent } from 'support/amplitude/amplitudeEvent';
import { QueryParams } from 'support/QueryParams';
import {
  ChallengeStatus,
  Mode,
  NotesContainerQuery,
  TrackStatus,
  useNotesContainerInviteQuery,
  useNotesContainerQuery,
  useNotesContainerStartChallengeMutation,
  useNotesContainerStartTrackMutation,
} from 'types/graphql';

import { useNotificationContext } from '../../../core/context/Notifications';
import ErrorMutation from '../../../core/legacy/components/core/Error';
import { createPathPrefix } from '../../../support/location';
import { getCurrentRegion } from '../../../support/LocationPing';
import { useEffectOnce } from '../../../support/react/hooks';
import { LabMetadataContext } from '../../context/LabMetadataContext';
import { NotesScreen } from '../../organisms/notes-screen/NotesScreen';
import { StartChallenge } from '../../organisms/start-challenge/StartChallenge';
import { TrackNotFound } from '../../organisms/track-not-found/TrackNotFound';
import embedEvent from '../../support/embedEvent';
import DarkOverlay from '../../templates/dark-overlay/DarkOverlay';

interface NotesContainerProps {
  teamSlug: string;
  trackSlug: string;
  challengeSlug: string;
  mode: Mode;
  inviteID?: string;
}

export function NotesContainer({ mode, teamSlug, trackSlug, challengeSlug, inviteID }: NotesContainerProps) {
  const { search, state } = useLocation<{ force?: boolean; refererURL?: string }>();
  const params = useMemo(() => new URLSearchParams(search), [search]);

  // Needed for cases where a teacher is impersonating a learner and skipping content
  const searchParams = new URLSearchParams(search);
  const userID = searchParams.get('userID');
  const searchInviteID = searchParams.get('inviteID');

  const invite = searchInviteID || inviteID || null;

  const customParametersRecord = useMemo(
    () => Object.fromEntries(Array.from(params).filter(([key]) => key.startsWith('icp_'))),
    [params],
  );
  const customParametersArray = useMemo(
    () =>
      Object.entries(customParametersRecord).map(([key, value]) => ({
        key: key.substring(4),
        value,
      })),
    [customParametersRecord],
  );

  const { data: inviteData } = useNotesContainerInviteQuery({
    variables: { inviteID: invite as string },
    skip: Boolean(!invite),
  });

  // Beware that the status field of track updates inside this component because of the polling inside StartChallenge.
  const { data, loading, error } = useNotesContainerQuery({
    variables: { teamSlug, trackSlug, challengeSlug, userID, inviteID: invite },
    // The first request should be fetched from the remote, to avoid displaying stale data in the challenge timeline.
    // Requests after this are allowed to come from the cache.
    fetchPolicy: 'network-only',
  });
  const refererURL = state?.refererURL || '';
  const [startTrack, { loading: startTrackLoading }] = useStartTrack();
  const { track, challenge } = data || {};
  const [startChallenge, { loading: startChallengeLoading }] = useStartChallenge({
    mode,
    inviteID,
    teamSlug,
    trackSlug,
    challengeSlug,
    refererURL,
  });
  const autoStart = params.has(QueryParams.AUTO_START);
  const forceStart = state?.force || false;
  const [, setCustomParameters] = useCustomParameters();
  const [challengeAlreadyStartedOnLoad, setChallengeAlreadyStartedOnLoad] = useState<boolean | null>(null);

  const { trackInvite } = inviteData || {};
  const showShareButton = !!trackInvite?.canClaim && trackInvite.enableLearnerSharing;

  useEffectOnce(
    () => {
      if (track) {
        setCustomParameters(customParametersRecord);
        startTrack(track, showShareButton, inviteID, mode, customParametersArray, forceStart);
      }
    },
    [track, startTrack, autoStart, forceStart],
    () =>
      !!track &&
      autoStart &&
      ((forceStart && track.status === TrackStatus.Started) || !isTrackStartingOrStarted(track)),
  );

  /**
   * We check if initially the challenge was already started when visiting the notes screen
   * If so, we redirect later.
   *
   * If it becomes started/completed later due to other reasons (e.g. skipping on notes screen)
   * Then we do not redirect
   */
  useEffect(() => {
    if (typeof challengeAlreadyStartedOnLoad === 'boolean' || !track || !challenge) {
      return;
    }

    setChallengeAlreadyStartedOnLoad(isChallengeStarted(track, challenge));
  }, [track, challenge, challengeAlreadyStartedOnLoad]);

  if (loading) {
    return <LoadingImage />;
  }

  if (error) {
    if (error.message.includes('Entity not found')) {
      return <TrackNotFound teamSlug={teamSlug} trackSlug={trackSlug} />;
    }

    return <ErrorMutation type="error" title="Error" message={error.message} />;
  }

  if (challengeAlreadyStartedOnLoad) {
    return <Redirect to={`${createPathPrefix({ mode, inviteID })}${challenge?.permalink}/assignment`} />;
  }

  if (track && challenge && !isTrackStartingOrStarted(track) && challenge.index !== 0) {
    return <Redirect to={`${createPathPrefix({ mode, inviteID })}${track.challenges[0].permalink}/notes`} />;
  }

  let returnTo: LinkProps['to'] | undefined;
  if (mode === Mode.Platform) {
    const pathname = inviteID ? `/${teamSlug}/invite/${inviteID}` : track?.permalink ?? undefined;
    returnTo = { pathname, state: state };
  }

  const skippingEnabled = isSkippingEnabled(data);

  return (
    <LabMetadataContext
      mode={mode}
      participantId={track?.participant?.id}
      teamSlug={teamSlug}
      trackId={track?.id}
      inviteId={inviteID}
      trackSkippable={skippingEnabled}
    >
      <DarkOverlay>
        {track && challenge ? (
          <>
            <Helmet>
              <title>{challenge.title ? `${challenge.title} - instruqt` : 'instruqt'}</title>
              <meta name="description" content={challenge.teaser} />
            </Helmet>
            <NotesScreen
              actions={
                isTrackStartingOrStarted(track) ? (
                  <StartChallenge
                    inviteID={invite}
                    userID={userID}
                    track={track}
                    challenge={challenge}
                    starting={startChallengeLoading}
                    onClickStart={() => startChallenge(challenge.id)}
                  />
                ) : (
                  <Button
                    disabled={startTrackLoading}
                    onClick={() =>
                      startTrack(track, showShareButton, inviteID, mode, customParametersArray, forceStart)
                    }
                  >
                    {startTrackLoading ? 'Starting...' : 'Start'}
                  </Button>
                )
              }
              challenges={track.challenges}
              currentChallenge={challenge}
              returnTo={returnTo}
              teamSlug={teamSlug}
              mode={mode}
              track={track}
              skippingEnabled={skippingEnabled}
              showShareButton={showShareButton}
              invite={trackInvite}
            />
          </>
        ) : null}
      </DarkOverlay>
    </LabMetadataContext>
  );
}

function useStartTrack() {
  const { dispatch } = useNotificationContext();
  const trackAmplitudeEvent = useTrackAmplitudeEvent();
  const [startTrackMutation, mutationResult] = useNotesContainerStartTrackMutation();
  const startTrack = async (
    track: NonNullable<NotesContainerQuery['track']>,
    showShareButton: boolean,
    inviteID?: string,
    mode?: Mode,
    customParameters?: { key: string; value: string }[],
    forceStart?: boolean,
  ) => {
    if (mutationResult.loading) return;
    const totalChallenges = track.challenges.length;

    embedEvent('track.started', {
      track_slug: track.slug,
      total_challenges: totalChallenges,
    });

    trackAmplitudeEvent({
      name: 'Lab - Track - Started',
      data: {
        showShareButton: showShareButton,
      },
    });

    let result;
    try {
      const region = await getCurrentRegion();
      result = await startTrackMutation({
        variables: {
          inviteID: inviteID ?? null,
          trackID: track.id,
          mode: mode ?? null,
          customParameters: customParameters ?? null,
          forceStart: forceStart ?? null,
          region: region,
        },
      });
    } catch (e: any) {
      dispatch('error', 'Failed to start the track', e.message, 10);
    }

    switch (result?.data?.startTrack?.status) {
      case TrackStatus.Failed:
      case TrackStatus.Stopped:
        dispatch(
          'error',
          'Failed to start the track',
          'Something went wrong while starting the track, please try again.',
          10,
        );
    }
  };

  return [startTrack, mutationResult] as const;
}

interface UseStartChallengeParams {
  mode: Mode;
  inviteID?: string;
  teamSlug: string;
  trackSlug: string;
  challengeSlug: string;
  refererURL: string;
}

const useStartChallenge = ({
  mode,
  inviteID,
  teamSlug,
  trackSlug,
  challengeSlug,
  refererURL,
}: UseStartChallengeParams) => {
  const history = useHistory();
  const { dispatch } = useNotificationContext();

  const [startChallengeMutation, mutationResult] = useNotesContainerStartChallengeMutation({
    onError(error) {
      dispatch('error', 'Failed to start the challenge', error.message, 10);
    },
  });

  const startChallenge = useCallback(
    async (id: string) => {
      embedEvent('track.challenge_started', {
        track_slug: trackSlug,
        challenge_slug: challengeSlug,
      });

      await startChallengeMutation({
        variables: { challengeID: id },
      });
      history.push(
        `${createPathPrefix({
          mode,
          inviteID,
        })}/${teamSlug}/tracks/${trackSlug}/challenges/${challengeSlug}/assignment`,
        {
          refererURL: refererURL,
        },
      );
    },
    [challengeSlug, history, mode, inviteID, teamSlug, startChallengeMutation, trackSlug, refererURL],
  );

  return [startChallenge, mutationResult] as const;
};

function isTrackStartingOrStarted(track: NonNullable<NotesContainerQuery['track']>) {
  return [TrackStatus.Creating, TrackStatus.Created, TrackStatus.Started].includes(track.status!);
}

function isChallengeStarted(
  track: NonNullable<NotesContainerQuery['track']>,
  challenge: NonNullable<NotesContainerQuery['challenge']>,
) {
  return (
    [TrackStatus.Created, TrackStatus.Started].includes(track.status!) &&
    [ChallengeStatus.Started, ChallengeStatus.Completed].includes(challenge.status)
  );
}

gql`
  mutation NotesContainerStartTrack(
    $trackID: String!
    $inviteID: String
    $mode: Mode
    $customParameters: [CustomParametersInput!]
    $forceStart: Boolean
    $region: String
  ) {
    startTrack(
      trackID: $trackID
      inviteID: $inviteID
      # TODO: Remove captcha in backend if captcha can be removed
      recaptchaToken: ""
      mode: $mode
      customParameters: $customParameters
      force: $forceStart
      region: $region
    ) {
      id
      status
      permalink
      challenges {
        id
        slug
        status
      }
    }
  }
`;

gql`
  mutation NotesContainerStartChallenge($challengeID: String!) {
    startChallenge(challengeID: $challengeID) {
      id
      status
      assignment
      tabs {
        type
        title
        target
      }
    }
  }
`;

gql`
  query notesContainer(
    $trackSlug: String
    $challengeSlug: String!
    $teamSlug: String!
    $inviteID: String
    $userID: String
  ) {
    track(trackSlug: $trackSlug, teamSlug: $teamSlug, userID: $userID, inviteID: $inviteID) {
      id
      permalink
      slug
      status
      creating
      completed
      median_starttime
      owner
      participant {
        id
      }
      challenges {
        id
        title
        status
        permalink
        ...ProgressChallenge
      }
    }

    challenge(
      trackSlug: $trackSlug
      teamSlug: $teamSlug
      challengeSlug: $challengeSlug
      userID: $userID
      inviteID: $inviteID
    ) {
      id
      index
      title
      permalink
      slug
      status
      teaser
      type
      failed
      notes {
        id
        type
        contents
        url
      }
    }

    ...IsSkippingEnabledQuery
  }
`;

gql`
  query notesContainerInvite($inviteID: String!) {
    trackInvite(inviteID: $inviteID) {
      id
      canClaim
      enableLearnerSharing
      allowAnonymous
      allowedEmailAddresses
    }
  }
`;
